import React, { useRef, useCallback } from 'react';

const SIZE = 110;
const CENTER = SIZE / 2;
const OUTER_R = 42;
const TICK_R = 38;
const ARROW_R = 30;

function polarToXY(angleDeg, r) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: CENTER + r * Math.cos(rad), y: CENTER + r * Math.sin(rad) };
}

const CompassDial = ({ azimuth = 0, onChange, onSnapView, disabled = false }) => {
  const svgRef = useRef(null);
  const draggingRef = useRef(false);

  const computeAngle = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    let deg = Math.atan2(dx, -dy) * (180 / Math.PI);
    if (deg < 0) deg += 360;
    return Math.round(deg) % 360;
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    draggingRef.current = true;
    const el = svgRef.current;
    if (el) el.setPointerCapture(e.pointerId);
    onChange?.(computeAngle(e.clientX, e.clientY));
  }, [disabled, computeAngle, onChange]);

  const handlePointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    onChange?.(computeAngle(e.clientX, e.clientY));
  }, [computeAngle, onChange]);

  const handlePointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  const arrowTip = polarToXY(azimuth, ARROW_R);
  const arrowTail = polarToXY(azimuth + 180, ARROW_R);
  const arrowHeadL = polarToXY(azimuth - 12, ARROW_R - 8);
  const arrowHeadR = polarToXY(azimuth + 12, ARROW_R - 8);
  const arrowTailL = polarToXY(azimuth + 180 - 12, ARROW_R - 8);
  const arrowTailR = polarToXY(azimuth + 180 + 12, ARROW_R - 8);

  const cardinals = [
    { label: 'N', angle: 0, dir: 'n' },
    { label: 'E', angle: 90, dir: 'e' },
    { label: 'S', angle: 180, dir: 's' },
    { label: 'W', angle: 270, dir: 'w' },
  ];

  return (
    <svg
      ref={svgRef}
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="compass-dial-svg"
      style={{ cursor: disabled ? 'default' : 'pointer', userSelect: 'none', overflow: 'visible' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onContextMenu={(e) => e.preventDefault()}
    >
      <circle cx={CENTER} cy={CENTER} r={OUTER_R} fill="#1f2937" stroke="#4b5563" strokeWidth="1.5" />

      {/* Degree ticks every 30 degrees */}
      {Array.from({ length: 12 }).map((_, i) => {
        const deg = i * 30;
        const p1 = polarToXY(deg, TICK_R);
        const p2 = polarToXY(deg, OUTER_R - 1);
        return (
          <line key={deg} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke="#6b7280" strokeWidth="1" />
        );
      })}

      {/* Double-headed arrow line */}
      <line x1={arrowTail.x} y1={arrowTail.y} x2={arrowTip.x} y2={arrowTip.y}
        stroke="#f9be0b" strokeWidth="2" />

      {/* Arrow head (tip) */}
      <polygon
        points={`${arrowTip.x},${arrowTip.y} ${arrowHeadL.x},${arrowHeadL.y} ${arrowHeadR.x},${arrowHeadR.y}`}
        fill="#f9be0b"
      />
      {/* Arrow head (tail) */}
      <polygon
        points={`${arrowTail.x},${arrowTail.y} ${arrowTailL.x},${arrowTailL.y} ${arrowTailR.x},${arrowTailR.y}`}
        fill="#f9be0b"
      />

      {/* Center area -- right-click for plan (bird's-eye) view */}
      <g
        className={onSnapView ? 'compass-center-group' : undefined}
        onContextMenu={(e) => {
          if (!onSnapView) return;
          e.preventDefault();
          e.stopPropagation();
          onSnapView('plan');
        }}
      >
        {onSnapView && <title>Right-click: Plan view</title>}
        <circle cx={CENTER} cy={CENTER} r={14} fill="transparent"
          style={{ pointerEvents: onSnapView ? 'all' : 'none' }} />
        <text x={CENTER} y={CENTER - 1} textAnchor="middle" dominantBaseline="central"
          fill="#e5e7eb" fontSize="10" fontWeight="600">
          {azimuth}°
        </text>
        {onSnapView && (
          <text x={CENTER} y={CENTER + 11} textAnchor="middle" dominantBaseline="central"
            className="compass-plan-hint" fontSize="7">
            PLAN
          </text>
        )}
      </g>

      {/* Cardinal labels -- right-click to snap camera to that cross-section */}
      {cardinals.map(({ label, angle, dir }) => {
        const pos = polarToXY(angle, OUTER_R + 9);
        return (
          <g key={label}
            className={onSnapView ? 'compass-cardinal' : undefined}
            onContextMenu={(e) => {
              if (!onSnapView) return;
              e.preventDefault();
              e.stopPropagation();
              onSnapView(dir);
            }}
          >
            {onSnapView && <title>Right-click: {label} cross-section</title>}
            <circle cx={pos.x} cy={pos.y} r={9} fill="transparent"
              style={{ pointerEvents: onSnapView ? 'all' : 'none' }} />
            <text x={pos.x} y={pos.y}
              textAnchor="middle" dominantBaseline="central"
              fill={label === 'N' ? '#f87171' : '#9ca3af'}
              fontSize="10" fontWeight={label === 'N' ? '700' : '600'}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(CompassDial);
